import { useForm } from "react-hook-form";
import { Box, Typography, TextField, Button, IconButton, Input } from "@mui/material";
import ArrowBackSvg from "@/assets/svg/ArrowBackSvg";
import theme from "@/theme";
import { FC, useRef, useState } from "react";
import ProfileSvg from "@/assets/svg/ProfileSvg";
import AddProfilePhotoSvg from "@/assets/svg/AddProfilePhotoSvg";
import PenSvg from "@/assets/svg/PenSvg";
import { useNavigate } from "react-router-dom";
import { useSendOtpEmailMutation } from "@/store/api/authApi";
import { RegisterUserRequest } from "@/store/api/authApiData";
import { styles } from "../stylesSignUp";

type FormInput = {
  bio: string;
};

interface CreateUsernameProps {
  setSignUpStep: React.Dispatch<React.SetStateAction<number>>;
  setSignUpData: (data: Partial<RegisterUserRequest>) => void;
  signUpData: RegisterUserRequest;
}

const MoreInformation: FC<CreateUsernameProps> = ({ setSignUpStep, setSignUpData, signUpData }) => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormInput>({
    // resolver: yupResolver(schemaMoreInformation),
    mode: "onChange",
  });
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [avatar, setAvatar] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const navigate = useNavigate();
  const [sendOtpEmail] = useSendOtpEmailMutation();

  const bio = watch("bio");
  const sendOtpAndNavigate = async (email: string) => {
    try {
      await sendOtpEmail({ email }).unwrap();
      navigate("/confirmation/verify-email", { state: { email } });
    } catch (err) {
      console.error("send OTP error:", err);
    }
  };

  const onSubmit = async (data: FormInput) => {
    const combinedData = {
      ...signUpData,
      ...data,
      bio: data.bio?.trim() || null,
      avatar: avatar,
    };
    setSignUpData(combinedData);

    try {
      await sendOtpEmail({ email: combinedData.email }).unwrap();

      navigate("/confirmation/verify-email", {
        state: { email: combinedData.email },
      });
    } catch (err) {
      console.error("send OTP error:", err);
    }
  };

  const skipDetailInfo = async () => {
    const skippedData = {
      ...signUpData,
      name: null,
      surname: null,
      bio: signUpData.bio?.trim() || null,
    };
    setSignUpData(skippedData);

    await sendOtpAndNavigate(skippedData.email);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ["image/jpeg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      alert("Only JPEG and PNG are allowed.");
      return;
    }
    setAvatar(file);
    setAvatarPreview(URL.createObjectURL(file));

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Box sx={styles.wrapper}>
      <Box position={"absolute"} left={"16px"} top={"16px"}>
        <IconButton sx={{ cursor: "pointer" }} onClick={() => setSignUpStep(3)}>
          <ArrowBackSvg />
        </IconButton>
      </Box>

      <Box position="absolute" top="16px" right={"16px"}>
        <Button
          sx={{
            cursor: "pointer",
            fontSize: "1rem",
            lineHeight: "1.25rem",
            color: theme.palette.custom.mainBlue,
            fontWeight: 500,
            textTransform: "none",
          }}
          onClick={() => skipDetailInfo()}
        >
          Skip
          <ArrowBackSvg
            color={theme.palette.custom.mainBlue}
            style={{ transform: "rotate(180deg)" }}
          />
        </Button>
      </Box>

      <Typography
        component="h1"
        variant="h2"
        color={theme.palette.custom.mainBlack}
        fontWeight={600}
        lineHeight={"2.25rem"}
        textAlign={"center"}
        sx={{
          marginBottom: "0.375rem",
        }}
      >
        Add more information
      </Typography>

      <Typography
        variant="h4"
        color="text.secondary"
        sx={{
          fontFamily: "Inter, sans-serif",
          fontWeight: 500,
          fontStyle: "normal",
          fontSize: "16px",
          lineHeight: "26px",
          letterSpacing: 0,
          textAlign: "center",
          maxWidth: "391px",
        }}
      >
        You can always change this information in your profile settings
      </Typography>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        display="flex"
        flexDirection="column"
        alignItems="center"
        sx={{ width: "100%", marginTop: "26px" }}
      >
        <Box
          width="5rem"
          height="5rem"
          borderRadius="50%"
          bgcolor={theme.palette.custom.mainBlueDisable}
          display="flex"
          alignItems="center"
          justifyContent="center"
          position="relative"
          overflow="visible"
          sx={{
            marginBottom: "22px",
          }}
        >
          <>
            {avatarPreview ? (
              <>
                <img
                  src={avatarPreview}
                  alt="Avatar"
                  style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
                />
                <IconButton
                  sx={{
                    position: "absolute",
                    right: -11,
                    bottom: -11,
                    cursor: "pointer",
                  }}
                >
                  <label htmlFor="avatar-upload">
                    <input
                      ref={fileInputRef}
                      id="avatar-upload"
                      type="file"
                      style={{ display: "none", cursor: "pointer" }}
                      onChange={(e) => handleFileChange(e)}
                    />
                    <PenSvg width={24} height={24} size="small" style={styles.svgIcon} />
                  </label>
                </IconButton>
              </>
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ProfileSvg />

                <IconButton
                  sx={{
                    position: "absolute",
                    right: -11,
                    bottom: -11,
                    cursor: "pointer",
                  }}
                >
                  <label htmlFor="avatar-upload">
                    <Input
                      id="avatar-upload"
                      type="file"
                      sx={{
                        display: "none",

                        cursor: "pointer",
                      }}
                      onChange={handleFileChange}
                    />
                    <AddProfilePhotoSvg style={styles.svgIcon} />
                  </label>
                </IconButton>
              </Box>
            )}
          </>
        </Box>
        <Box
          sx={{
            width: "100%",
            mb: "1.5rem",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          <Box sx={styles.inputLabel}>Bio</Box>

          <TextField
            multiline
            rows={4}
            placeholder="Enter your Bio"
            fullWidth
            {...register("bio")}
            error={!!errors.bio}
            helperText={errors.bio?.message}
            InputProps={styles.TextFieldProps}
          />
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={(bio?.length ?? 0) <= 3}
          sx={styles.continueButton}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );
};

export default MoreInformation;
